<template>
  <div
    class="fts-automation-node-start automation-node"
    :class="{
      'automation-node--debug': data.debug,
      'automation-node--debugging': isDebugging,
      'fts-automation-node-start--wide': showSyncStatus,
    }"
  >
    <fts-automation-block-number :data="data" />

    <div class="node-main">
      <div class="node-icon">
        <font-awesome-icon :icon="['fas', 'play']" />
      </div>

      <div class="node-text">Start</div>

      <!-- Trigger-only sync status (formsync-declared-triggers-proposal.md D-8, r33136 automatic
           path): with nothing connected to Start, the trigger watches ARE the sync set. Collapsed
           to an icon — the tooltip carries the effective combined fields, or the analyzer's
           diagnostic when the form still syncs broadly. Form automations only. -->
      <div class="node-sync" v-if="showSyncStatus" @mousedown.stop @pointerdown.stop>
        <fts-tippy>
          <div
            class="node-sync__icon"
            :class="{
              'node-sync__icon--always': verdictAlways,
              'node-sync__icon--none': !verdictAlways && !syncsOn.length,
            }"
          >
            <font-awesome-icon :icon="['fas', 'sync-alt']" />
          </div>
          <template #content>
            <div class="tippy-hint sync-chip-tip">
              <template v-if="verdictAlways">
                <div class="tippy-hint-highlight">Every edit syncs</div>
                <div>{{ verdictAlwaysReason }}</div>
              </template>
              <template v-else-if="syncsOn.length">
                <div class="tippy-hint-highlight">Syncs on</div>
                <div class="sync-chip-tip-reason" v-for="item in syncsOn" :key="item.label">
                  <span class="sync-chip-tip-what">{{ item.label }}</span>
                  <span class="sync-chip-tip-kind" v-if="item.reasons">{{ item.reasons }}</span>
                </div>
              </template>
              <div v-else>No edit syncs for this automation.</div>
            </div>
          </template>
        </fts-tippy>
      </div>

      <div class="node-debug">
        <fts-automation-debugpoint v-model="data.debug" class="node-header__debugpoint" />
      </div>
    </div>
  </div>
</template>

<script>
import { automationDesignStore } from "../../store/automationDesignStore";

import FTSAutomationScopedVariables from "./FTSAutomationScopedVariables";
import FTSAutomationDebugpoint from "./FTSAutomationDebugpoint";
import FTSAutomationBlockNumber from "./FTSAutomationBlockNumber";
import FTSTippy from "../../elements/FTSTippy";

export default {
  props: {
    data: Object,
    information: Array,
  },
  data() {
    return {
      editor: null,
      show: -1,
      content: "",
      isDebugging: false,
    };
  },
  components: {
    "fts-automation-scoped-variables": FTSAutomationScopedVariables,
    "fts-automation-debugpoint": FTSAutomationDebugpoint,
    "fts-automation-block-number": FTSAutomationBlockNumber,
    "fts-tippy": FTSTippy,
  },
  computed: {
    variables() {
      return automationDesignStore.state.variables;
    },
    // Form automations only — event/schedule automations have no sync loop to report on.
    isFormAutomation() {
      return !!automationDesignStore.state.form;
    },
    verdict() {
      return automationDesignStore.state.syncVerdict;
    },
    showSyncStatus() {
      return this.isFormAutomation && !!this.verdict;
    },
    verdictAlways() {
      return !!(this.verdict && this.verdict.always && this.verdict.always.length);
    },
    verdictAlwaysReason() {
      if (!this.verdictAlways) return "";
      if (this.verdict.automationDiagnostic) return this.verdict.automationDiagnostic;
      const reasons = this.verdict.alwaysReasons;
      return reasons && reasons.length ? reasons.join("; ") : this.verdict.always.join("; ");
    },
    // D-8 combined list rows: every field the verdict says round-trips, with its reasons
    // (formula / automation / script). One row per field — the verdict may list the same
    // field once per origin, so merge rows sharing a label (also keeps :key unique).
    syncsOn() {
      if (!this.verdict || this.verdictAlways || !this.verdict.fields) return [];
      const byLabel = new Map();
      this.verdict.fields.forEach(field => {
        const label = field.secondary ? `${field.primary} › ${field.secondary}` : field.primary;
        const entry = byLabel.get(label) || { label, reasons: [] };
        entry.reasons.push(...(field.targets || []).map(t => t.origin || t.primary));
        byLabel.set(label, entry);
      });
      return Array.from(byLabel.values()).map(entry => ({
        label: entry.label,
        reasons: entry.reasons
          .filter((v, i, arr) => v && arr.indexOf(v) === i)
          .join(", "),
      }));
    },
  },
  methods: {
    refreshBlockState() {
      this.isDebugging = false;
      if (automationDesignStore.state.debugPayload && automationDesignStore.state.debugPayload.nodeId) {
        let nodeId = automationDesignStore.state.debugPayload.nodeId;
        let diagram = automationDesignStore.state.diagram;
        /* the debugged node may have been deleted since the payload was captured */
        if (diagram && diagram[nodeId] && diagram[nodeId].data.id == this.data.id) {
          this.isDebugging = true;
        }
      }

      this.$forceUpdate();
    },
  },
  mounted() {
    window.eventBus.$on("refreshAutomationBlocks", this.refreshBlockState);
  },
  beforeDestroy() {
    /* this handler only — a bare $off would strip every other block's listener too */
    window.eventBus.$off("refreshAutomationBlocks", this.refreshBlockState);
  },
};
</script>

<style lang="scss" scoped>
@use "./src/assets/scss/vars.scss" as *;

.fts-automation-node-start {
  width: 85px;
  padding: 0.5rem;
  /* room for the sync icon */
  &--wide {
    width: 110px;
  }
  .node-main {
    display: flex;
    align-items: center;
  }
  .node-sync {
    flex: none;
    margin-right: 0.375rem;
    .node-sync__icon {
      height: 1rem;
      width: 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 10em;
      background-color: rgba(var(--fts-block-start-background-rgb), 0.15);
      color: var(--fts-block-start-background);
      font-size: 0.5rem;
      /* every-edit-sync is the costly verdict — flag it before hover, in the
         same red the form designer's .design-element-sync-chip--always uses */
      &--always {
        background-color: rgba(244, 67, 54, 0.14);
        color: #c62828;
      }
      &--none {
        opacity: 0.45;
      }
    }
  }
  .node-icon {
    height: 1rem;
    width: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 0.375rem;
    border-radius: 10em;
    background-color: rgba(var(--fts-block-start-background-rgb), 0.15);
    color: var(--fts-block-start-background);
    font-size: 0.5rem;
  }
  .node-text {
    flex: 1;
  }
  .node-debug {
    position: relative;
    height: 1.25rem;
    width: 1.25rem;
  }
  .node-header__debugpoint {
    @include absolute-center-y;
    right: 0;
  }
  &:hover {
    background-color: var(--fts-block-start-background);
    color: var(--fts-light);
    .node-icon,
    .node-sync__icon {
      color: inherit;
    }
  }
}
</style>